import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
  output,
} from '@angular/core';
import { CommunityItem, PostTypes } from '../../../../interfaces';
import { SliderComponent, SliderImage } from '../../../slider/slider.component';
import { UnixDateFormatterPipe } from '../../../../pipes/unix-date-formatter.pipe';
import { TextContentComponent } from '../text-content/text-content.component';
import { FeatureFlagService } from '../../../../services/util/feature-flag.service';
import { FileItemComponent } from '../file-item/file-item.component';
import { IconComponent } from '@outlaw/icons';
import { CommunityMobileEventsService } from '../../../../services/util/mobile-events.service';
import { CommunityNavigationService, PaymentService } from '../../../../services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DataTestDirective, NavigationService, TargetUrl } from '@outlaw/core';
import { ExternalLinkDirective } from '../../../../directives/external-link.directive';

@Component({
  selector: 'ol-post-text',
  templateUrl: './post-text.component.html',
  styleUrls: ['./post-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SliderComponent,
    UnixDateFormatterPipe,
    TextContentComponent,
    FileItemComponent,
    DataTestDirective,
    ExternalLinkDirective,
    IconComponent,
  ],
})
export class PostTextComponent implements OnInit {
  item = input.required<CommunityItem>();
  isGuest = input.required<boolean>();
  variant = input<'primary' | 'secondary'>('primary');
  openInSameWindow = input<boolean>(false);
  isMajiApp = input<boolean>(false);

  navigateToPost = output<void>();

  mappedImages = signal<SliderImage[]>([]);
  postAccess = false;

  private mobileEventsService = inject(CommunityMobileEventsService);
  private navigationService = inject(NavigationService);
  private communityNavigationService = inject(CommunityNavigationService);
  private featureFlagService = inject(FeatureFlagService);
  private paymentService = inject(PaymentService);
  private destroyRef = inject(DestroyRef);

  postUrl = computed(() => {
    return this.variant() === 'primary' ? this.communityNavigationService.getPostUrl(this.item().post_uuid) : null;
  });

  showShowFiles = computed(() => {
    return this.item().content.files?.length && this.featureFlagService.isPaidPostsEnabled();
  });

  linkTarget = computed(() => (this.openInSameWindow() ? TargetUrl.sameWindow : TargetUrl.newWindow));

  isWebView = computed(() => this.isMajiApp() && this.isMobile);

  get requiresPurchase(): boolean {
    return !this.isGuest() && !this.postAccess && this.featureFlagService.isPaidPostsEnabled();
  }

  get isMobile(): boolean {
    return this.mobileEventsService.isWebView;
  }

  ngOnInit() {
    this.checkPostAccess(this.item(), this.isGuest());

    this.mappedImages.set(
      this.item().content.images.map(img => ({
        imageUrl: img.image_url,
        url: this.postUrl(),
        alt: img.image_url,
      }))
    );
  }

  onSubscribeClick(event: Event): void {
    event.stopPropagation();

    if (this.variant() === 'primary') {
      const postLink = this.communityNavigationService.getPostUrl(this.item().post_uuid);
      history.pushState({}, '', postLink);
    }

    this.navigationService.goToSubscriptionPage({
      postId: this.item().post_uuid,
    });
  }

  onPostClick(): void {
    this.navigateToPost.emit();
  }

  private checkPostAccess(post: CommunityItem, isGuest: boolean) {
    if (isGuest || post.post_type.slug !== PostTypes.postPaid || !this.featureFlagService.isPaidPostsEnabled()) {
      this.postAccess = true;
      return;
    }

    this.paymentService
      .checkPostAccess(post.post_uuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(r => {
        this.postAccess = r.has_access;
      });
  }
}
